import { useApi } from '@backstage/core-plugin-api';
import { CatalogApi, catalogApiRef } from '@backstage/plugin-catalog-react';
import { useEffect, useState } from 'react';
import { MAINTAINED_BY_RELATION } from './constant.tsx';
import { ACCOUNT_ENABLED_ANNOTATION } from '../users/constant.tsx';

export interface Component {
  ref: string;
  kind: string;
  name: string;
  namespace: string;
}

export interface MaintainerRow {
  components: Component[];
  displayName: string;
  kind: string;
  name: string;
  namespace: string;
  /** true = compte Entra actif, false = désactivé (LDAP inactif) */
  accountEnabled: boolean;
}

export function useMaintainersMetrics() {
  const catalogApi: CatalogApi = useApi(catalogApiRef);
  const [data, setData] = useState<MaintainerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function load(): Promise<void> {
      try {
        const [{ items: components }, { items: users }] = await Promise.all([
          catalogApi.getEntities({
            filter: { kind: 'Component' },
            fields: [
              'kind',
              'metadata.name',
              'metadata.namespace',
              'relations',
            ],
          }),
          catalogApi.getEntities({
            filter: { kind: 'User' },
            fields: [
              'kind',
              'metadata.name',
              'metadata.namespace',
              'metadata.annotations',
              'spec'
            ],
          }),
        ]);

        const explainedUsers = new Map<
          string,
          { displayName: string; kind: string; name: string; namespace: string; accountEnabled: boolean }
        >();
        for (const user of users) {
          const kind = user.kind.toLowerCase();
          const name = user.metadata.name;
          const namespace = user.metadata.namespace ?? 'default';
          const displayName = (user.spec as any)?.profile?.displayName ?? user.metadata.name;
          const ref = `${kind}:${namespace}/${name}`;
          const accountEnabled =
            user.metadata.annotations?.[ACCOUNT_ENABLED_ANNOTATION] !== 'false';
          explainedUsers.set(ref.toLowerCase(), { displayName, kind, name, namespace, accountEnabled });
        }

        const maintainers = new Map<string, { userRef: string; components: Component[] }>();
        for (const component of components) {
          const maintainedByRelations = (component.relations ?? []).filter(
            maintainedByRelation => maintainedByRelation.type === MAINTAINED_BY_RELATION,
          );
          if (maintainedByRelations.length === 0) continue;

          const maintainerComponent: Component = {
            ref: `${component.kind.toLowerCase()}:${
              component.metadata.namespace ?? 'default'
            }/${component.metadata.name}`,
            kind: component.kind.toLowerCase(),
            name: component.metadata.name,
            namespace: component.metadata.namespace ?? 'default',
          };

          for (const maintainedByRelation of maintainedByRelations) {
            const userRef = maintainedByRelation.targetRef;

            if (!maintainers.has(userRef)) {
              maintainers.set(userRef, { userRef, components: [] });
            }
            maintainers.get(userRef)!.components.push(maintainerComponent);
          }
        }

        const maintainerRows: MaintainerRow[] = Array.from(
          maintainers.entries(),
        )
          .filter(([userRef]) => explainedUsers.has(userRef.toLowerCase()))
          .map(([userRef, { components: userComponents }]) => {
            const explainedUser = explainedUsers.get(userRef.toLowerCase());
            return {
              components: userComponents.sort((a, b) =>
                a.name.localeCompare(b.name),
              ),
              kind: explainedUser!.kind,
              displayName: explainedUser!.displayName,
              name: explainedUser!.name,
              namespace: explainedUser!.namespace,
              accountEnabled: explainedUser!.accountEnabled,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name));

        setData(maintainerRows);
      } catch (e: any) {
        setError(e);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [catalogApi]);

  return { data, loading, error };
}